import { useEffect } from 'react';
import Highlight from '@tiptap/extension-highlight';
import { CharacterCount } from '@tiptap/extensions';
import { type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { type Editor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { logger } from '@zep/core-logger';
import { MathInline } from '@/shared/features/rich-text';
import { useEditMode } from '../../focus/useIsEditMode';
import { openEquationEditorModal } from '../EquationEditorModal';

/**
 * 퀴즈 생성 시 싱글톤 텍스트 에디터를 제공하는 hook
 */
export const useBuildTextEditor = ({
  content,
  onUpdate
}: {
  content?: string;
  onUpdate?: (content: string) => void;
}) => {
  const {
    onEditModeChange
  } = useEditMode();
  const editor = useEditor({
    extensions: [StarterKit, CharacterCount.configure({
      limit: 300,
      mode: 'textSize'
    }), Highlight.configure({
      multicolor: true
    }),
    // Custom MathInline extension with onClick handler
    MathInline.configure({
      onClick: async (node: ProseMirrorNode, pos: number, editor: Editor) => {
        const latex = await openEquationEditorModal({
          initialValue: node?.attrs?.latex ?? ''
        });
        if (latex) {
          editor.chain().setNodeSelection(pos).updateAttributes('mathInline', {
            latex
          }).focus().run();
          editor.commands.focus('end');
        }
        logger.track('build_addformula', {
          location: 'question'
        });
      }
    })],
    immediatelyRender: false,
    content,
    onUpdate: ({
      editor
    }) => {
      const html = editor.getHTML();
      onUpdate?.(editor.isEmpty ? '' : html);
    },
    parseOptions: {
      preserveWhitespace: 'full'
    },
    editorProps: {
      attributes: {
        class: 'w-full border-none focus-within:outline-none focus:outline-none focus-visible:outline-none',
        'data-no-focus-visible': 'true'
      }
    }
  });

  // content가 변경되었을 때 에디터 내용을 업데이트
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML();
      if (currentContent !== content) {
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);
  useEffect(() => {
    if (editor) {
      editor.on('focus', () => {
        onEditModeChange(true);
      });
    }
    // return () => {
    //   editor?.off('focus');
    //   editor?.off('blur');
    // };
  }, [editor, onEditModeChange]);
  return {
    editor
  };
};